import { toast } from "react-hot-toast";
import { useI18nStore } from "../stores/i18nStore";

interface ToastOptions {
  duration?: number;
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  icon?: string;
}

// Toasts whose message is a translation key
export const showToast = {
  success: (messageKey: string, name?: string) => {
    const { t } = useI18nStore.getState();
    const message = name
      ? `${t[messageKey as keyof typeof t]}, ${name}!`
      : t[messageKey as keyof typeof t];
    toast.success(message);
  },

  error: (messageKey: string) => {
    const { t } = useI18nStore.getState();
    toast.error(t[messageKey as keyof typeof t]);
  },

  info: (messageKey: string) => {
    const { t } = useI18nStore.getState();
    toast(t[messageKey as keyof typeof t]);
  },
};

// Localized toast messages for the HealthPass app
export const i18nToast = {
  // Existing methods, kept for compatibility
  loginSuccess: (firstName: string) => {
    const { t } = useI18nStore.getState();
    toast.success(`${t.loginSuccessful}, ${firstName}!`);
  },

  registrationSuccess: (firstName: string) => {
    const { t } = useI18nStore.getState();
    toast.success(`${t.registrationSuccessful}, ${firstName}!`);
  },

  loginFailed: () => showToast.error("loginFailed"),
  registrationFailed: () => showToast.error("registrationFailed"),
  logoutSuccess: () => showToast.success("logoutSuccessful"),
  sessionExpired: () => showToast.error("sessionExpired"),
  networkError: () => showToast.error("networkError"),
  accessDenied: () => showToast.error("accessDenied"),
  validationError: () => showToast.error("validationError"),
  tooManyRequests: () => showToast.error("tooManyRequests"),
  serverError: () => showToast.error("serverError"),

  // General methods (the caller passes an already translated message)
  success: (message: string, options?: ToastOptions) => {
    toast.success(message, {
      duration: 4000,
      position: "top-right",
      ...options,
    });
  },

  error: (message: string, options?: ToastOptions) => {
    toast.error(message, {
      duration: 6000,
      position: "top-right",
      ...options,
    });
  },

  info: (message: string, options?: ToastOptions) => {
    toast(message, {
      icon: options?.icon || "ℹ️",
      duration: 4000,
      position: "top-right",
      ...options,
    });
  },

  loading: (message: string) => {
    return toast.loading(message, {
      position: "top-right",
    });
  },

  dismiss: (toastId: string) => {
    toast.dismiss(toastId);
  },

  // Authentication messages
  loginSuccessful: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastLoginWelcome, {
      duration: 4000,
      position: "top-right",
      icon: "👋",
    });
  },

  passwordResetSent: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastPasswordResetSent, {
      duration: 5000,
      position: "top-right",
      icon: "📧",
    });
  },

  // Document messages
  documentUploaded: (count: number = 1) => {
    const { t } = useI18nStore.getState();
    const message =
      count === 1
        ? t.miscToastDocumentUploaded
        : t.miscToastDocumentsUploaded.replace("{count}", String(count));

    toast.success(message, {
      duration: 4000,
      position: "top-right",
      icon: "🔒",
    });
  },

  documentUploadFailed: (error?: string) => {
    const { t } = useI18nStore.getState();
    const message = error
      ? t.miscToastUploadFailedWithReason.replace("{error}", error)
      : t.miscToastUploadFailed;

    toast.error(message, {
      duration: 6000,
      position: "top-right",
    });
  },

  documentDeleted: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastDocumentDeleted, {
      duration: 3000,
      position: "top-right",
    });
  },

  documentDownloaded: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastDocumentDownloaded, {
      duration: 3000,
      position: "top-right",
      icon: "⬇️",
    });
  },

  // Health data
  healthRecordSaved: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastHealthRecordSaved, {
      duration: 4000,
      position: "top-right",
      icon: "🏥",
    });
  },

  vaccinationAdded: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastVaccinationAdded, {
      duration: 4000,
      position: "top-right",
      icon: "💉",
    });
  },

  medicationAdded: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastMedicationAdded, {
      duration: 4000,
      position: "top-right",
      icon: "💊",
    });
  },

  labResultsSaved: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.labResultsSaved, {
      duration: 4000,
      position: "top-right",
      icon: "🧪",
    });
  },

  // ✅ NEW: Wellbeing-specific toast messages
  wellbeingDataSaved: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastWellbeingSaved, {
      duration: 4000,
      position: "top-right",
      icon: "💝",
    });
  },

  wellbeingDataSaveFailed: () => {
    const { t } = useI18nStore.getState();
    toast.error(t.miscToastWellbeingSaveFailed, {
      duration: 6000,
      position: "top-right",
    });
  },

  securityAlert: (message: string) => {
    const { t } = useI18nStore.getState();
    toast.error(t.miscToastSecurityAlert.replace("{message}", message), {
      duration: 8000,
      position: "top-center",
      icon: "⚠️",
    });
  },

  // AI Chat (Tina)
  tinaConnected: () => {
    const { t } = useI18nStore.getState();
    toast.success(t.miscToastTinaReady, {
      duration: 3000,
      position: "top-right",
      icon: "🤖",
    });
  },

  tinaError: () => {
    const { t } = useI18nStore.getState();
    toast.error(t.miscToastTinaUnavailable, {
      duration: 5000,
      position: "top-right",
    });
  },

  // Custom toast for complex messages
  custom: (
    message: string,
    type: "success" | "error" | "loading" | "blank",
    options?: ToastOptions,
  ) => {
    const { icon, ...toastOptions } = options || {};

    switch (type) {
      case "success":
        return toast.success(
          message,
          icon ? { ...toastOptions, icon } : toastOptions,
        );
      case "error":
        return toast.error(
          message,
          icon ? { ...toastOptions, icon } : toastOptions,
        );
      case "loading":
        return toast.loading(message, toastOptions);
      default:
        return toast(message, icon ? { ...toastOptions, icon } : toastOptions);
    }
  },
};

// Helpers for common toast combinations
export const showApiError = (error: any) => {
  const message =
    error?.response?.data?.error?.message ||
    error?.message ||
    useI18nStore.getState().t.miscToastUnknownError;

  if (error?.response?.status === 401) {
    i18nToast.sessionExpired();
  } else if (error?.response?.status === 403) {
    i18nToast.accessDenied();
  } else if (error?.response?.status >= 500) {
    i18nToast.serverError();
  } else if (!navigator.onLine) {
    i18nToast.networkError();
  } else {
    i18nToast.error(message);
  }
};

export const showLoadingToast = (message: string) => {
  return i18nToast.loading(message);
};

export const updateLoadingToast = (
  toastId: string,
  message: string,
  type: "success" | "error",
) => {
  toast.dismiss(toastId);
  if (type === "success") {
    i18nToast.success(message);
  } else {
    i18nToast.error(message);
  }
};
